
import React, { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { 
  BookOpen,
  Target,
  Users,
  Clock,
  Calendar,
  DollarSign,
  Award,
  Video,
  FileText,
  Link,
  Save,
  Send,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Plus,
  Trash2,
  Upload,
  Play,
  Pause,
  Settings,
  Eye,
  Star,
  TrendingUp,
  Brain,
  Zap
} from 'lucide-react'
import { usePageTitle } from '@/stores/page-title-store'

export default function NewTrainingPage() {
  const { user } = useAuth()
  usePageTitle('Novo Treinamento')
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState({
    // Basic Information
    basicInfo: {
      title: '',
      category: '',
      type: 'presencial' as 'presencial' | 'online' | 'hibrido',
      level: 'iniciante' as 'iniciante' | 'intermediario' | 'avancado',
      tags: [] as string[],
      description: '',
      objectives: [] as string[]
    },
    // Content & Structure
    content: {
      modules: [] as Array<{
        title: string
        description: string
        duration: number
        lessons: Array<{
          title: string
          type: 'video' | 'texto' | 'quiz' | 'exercicio'
          duration: number
          content: string
        }>
      }>,
      materials: [] as Array<{
        name: string
        type: 'pdf' | 'video' | 'link' | 'arquivo'
        url: string
        description: string
      }>,
      totalDuration: 0,
      hasQuiz: false,
      hasCertificate: false
    },
    // Target Audience & Prerequisites
    audience: {
      targetRoles: [] as string[],
      departments: [] as string[],
      prerequisites: [] as string[],
      maxParticipants: 0,
      minParticipants: 1
    },
    // Scheduling & Logistics
    schedule: {
      startDate: '',
      endDate: '',
      sessions: [] as Array<{
        date: string
        startTime: string
        endTime: string
        location: string
        instructor: string
      }>,
      timezone: 'America/Sao_Paulo',
      isRecurring: false,
      recurringPattern: ''
    },
    // Instructor & Resources
    resources: {
      instructor: {
        name: '',
        bio: '',
        credentials: [] as string[],
        photo: ''
      },
      budget: 0,
      location: '',
      equipment: [] as string[],
      platform: ''
    },
    // Assessment & Certification
    assessment: {
      hasPreAssessment: false,
      hasPostAssessment: false,
      passingScore: 70,
      evaluationCriteria: [] as string[],
      certificateTemplate: '',
      knowledgeMultiplication: false
    }
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Form dropdown options
  const trainingCategories = [
    'Técnico',
    'Comportamental',
    'Liderança',
    'Comercial',
    'Digital',
    'Criatividade',
    'Estratégia',
    'Compliance',
    'Diversidade & Inclusão',
    'Sustentabilidade',
    'Tecnologia',
    'Inovação'
  ]

  const roles = [
    'Todos os Colaboradores',
    'Liderança',
    'Estagiários',
    'Analistas',
    'Coordenadores',
    'Gerentes',
    'Diretores',
    'Novos Colaboradores'
  ]

  const departments = [
    'Todos os Departamentos',
    'Estratégia',
    'Criação',
    'Produção',
    'Atendimento',
    'Administrativo',
    'Financeiro',
    'RH',
    'TI',
    'Digital'
  ]

  const instructors = [
    { id: 'inst-1', name: 'Patricia Lima', specialty: 'Liderança', bio: 'Especialista em desenvolvimento de liderança' },
    { id: 'inst-2', name: 'Carlos Silva', specialty: 'Estratégia', bio: 'Consultor em estratégia de negócios' },
    { id: 'inst-3', name: 'Maria Santos', specialty: 'Digital', bio: 'Expert em marketing digital' },
    { id: 'inst-4', name: 'Externo', specialty: 'Diversos', bio: 'Instrutor externo especializado' }
  ]

  const availableTags = [
    'obrigatório',
    'certificação',
    'hands-on',
    'teórico',
    'prático',
    'beginner-friendly',
    'avançado',
    'soft-skills',
    'hard-skills',
    'cultura',
    'processo',
    'ferramenta',
    'metodologia'
  ]

  const steps = [
    { id: 1, title: 'Informações Básicas', icon: BookOpen },
    { id: 2, title: 'Conteúdo', icon: FileText },
    { id: 3, title: 'Público-Alvo', icon: Users },
    { id: 4, title: 'Cronograma', icon: Calendar },
    { id: 5, title: 'Recursos', icon: Settings },
    { id: 6, title: 'Avaliação', icon: Award },
    { id: 7, title: 'Revisão', icon: CheckCircle }
  ]

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {}

    switch (step) {
      case 1:
        if (!formData.basicInfo.title) newErrors.title = 'Título é obrigatório'
        if (!formData.basicInfo.category) newErrors.category = 'Categoria é obrigatória'
        if (!formData.basicInfo.description) newErrors.description = 'Descrição é obrigatória'
        if (formData.basicInfo.objectives.length === 0) newErrors.objectives = 'Adicione pelo menos um objetivo'
        break
      case 2:
        if (formData.content.modules.length === 0) newErrors.modules = 'Adicione pelo menos um módulo'
        break
      case 3:
        if (formData.audience.targetRoles.length === 0) newErrors.targetRoles = 'Selecione pelo menos um público-alvo'
        break
      case 4:
        if (!formData.schedule.startDate) newErrors.startDate = 'Data de início é obrigatória'
        if (formData.schedule.sessions.length === 0) newErrors.sessions = 'Adicione pelo menos uma sessão'
        break
      case 5:
        if (!formData.resources.instructor.name) newErrors.instructor = 'Instrutor é obrigatório'
        break
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length))
    }
  }

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1))
  }

  const handleInputChange = (section: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...(prev as any)[section],
        [field]: value
      }
    }))
  }

  const handleNestedInputChange = (section: string, nested: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...(prev as any)[section],
        [nested]: {
          ...(prev as any)[section][nested],
          [field]: value
        }
      }
    }))
  }

  const addToArray = (section: string, field: string, value: string) => {
    if (!value.trim()) return
    
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...(prev as any)[section],
        [field]: [...(prev as any)[section][field], value.trim()]
      }
    }))
  }

  const removeFromArray = (section: string, field: string, index: number) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...(prev as any)[section],
        [field]: (prev as any)[section][field].filter((_: any, i: number) => i !== index)
      }
    }))
  }

  const addModule = () => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        modules: [...prev.content.modules, {
          title: '',
          description: '',
          duration: 0,
          lessons: []
        }]
      }
    }))
  }

  const removeModule = (index: number) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        modules: prev.content.modules.filter((_, i) => i !== index)
      }
    }))
  }

  const addSession = () => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        sessions: [...prev.schedule.sessions, {
          date: '',
          startTime: '',
          endTime: '',
          location: '',
          instructor: ''
        }]
      }
    }))
  }

  const removeSession = (index: number) => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        sessions: prev.schedule.sessions.filter((_, i) => i !== index)
      }
    }))
  }

  const handleSubmit = async () => {
    if (!validateStep(6)) return

    setIsSubmitting(true)
    try {
      // Generate training ID
      const trainingId = `TRN-${Date.now()}`
      
      const newTraining = {
        ...formData,
        id: trainingId,
        status: 'draft',
        createdBy: user?.id,
        createdAt: new Date().toISOString(),
        enrollments: 0
      }

      // Here you would save to your backend
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      alert('Treinamento criado com sucesso!')
      // Redirect to training list
      window.location.href = '/training'
      
    } catch (error) {
      console.error('Erro ao criar treinamento:', error)
      alert('Erro ao criar treinamento. Tente novamente.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Título */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Título do Treinamento *
                </label>
                <div className="relative">
                  <BookOpen className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    value={formData.basicInfo.title}
                    onChange={(e) => handleInputChange('basicInfo', 'title', e.target.value)}
                    className={`w-full bg-brand-dark border rounded-monumenta pl-10 pr-4 py-3 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none ${errors.title ? 'border-brand-red' : 'border-gray-700'}`}
                    placeholder="Ex: Fundamentos de Marketing Digital, Liderança Ágil, Design Thinking"
                  />
                  {errors.title && <p className="text-brand-red text-sm mt-1">{errors.title}</p>}
                </div>
              </div>

              {/* Categoria */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Categoria *
                </label>
                <div className="relative">
                  <Target className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <select
                    value={formData.basicInfo.category}
                    onChange={(e) => handleInputChange('basicInfo', 'category', e.target.value)}
                    className={`w-full bg-brand-dark border rounded-monumenta pl-10 pr-4 py-3 text-white focus:border-brand-primary focus:outline-none ${errors.category ? 'border-brand-red' : 'border-gray-700'}`}
                  >
                    <option value="">Selecione a categoria</option>
                    {trainingCategories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                  {errors.category && <p className="text-brand-red text-sm mt-1">{errors.category}</p>}
                </div>
              </div>

              {/* Nível */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nível
                </label>
                <div className="flex space-x-4">
                  {['iniciante', 'intermediario', 'avancado'].map(level => (
                    <label key={level} className="flex items-center">
                      <input
                        type="radio"
                        name="level"
                        value={level}
                        checked={formData.basicInfo.level === level}
                        onChange={(e) => handleInputChange('basicInfo', 'level', e.target.value)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 rounded-full border-2 mr-2 ${formData.basicInfo.level === level ? 'border-brand-primary bg-brand-primary' : 'border-gray-400'}`}>
                        {formData.basicInfo.level === level && (
                          <div className="w-2 h-2 bg-brand-dark rounded-full mx-auto mt-0.5"></div>
                        )}
                      </div>
                      <span className="text-white capitalize">{level}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Tipo de Treinamento */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-4">
                Tipo de Treinamento
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { value: 'presencial', label: 'Presencial', description: 'Treinamento no escritório ou local específico', icon: Users },
                  { value: 'online', label: 'Online', description: 'Treinamento totalmente virtual', icon: Video },
                  { value: 'hibrido', label: 'Híbrido', description: 'Combinação de presencial e online', icon: Zap }
                ].map(option => {
                  const Icon = option.icon
                  return (
                    <label key={option.value} className="cursor-pointer">
                      <input
                        type="radio"
                        name="type"
                        value={option.value}
                        checked={formData.basicInfo.type === option.value}
                        onChange={(e) => handleInputChange('basicInfo', 'type', e.target.value)}
                        className="sr-only"
                      />
                      <div className={`p-4 border-2 rounded-monumenta transition-colors ${
                        formData.basicInfo.type === option.value 
                          ? 'border-brand-primary bg-brand-primary/10' 
                          : 'border-gray-600 hover:border-gray-500'
                      }`}>
                        <Icon size={24} className={`mb-2 ${formData.basicInfo.type === option.value ? 'text-brand-primary' : 'text-gray-400'}`} />
                        <h3 className={`font-medium ${formData.basicInfo.type === option.value ? 'text-brand-primary' : 'text-white'}`}>
                          {option.label}
                        </h3>
                        <p className="text-sm text-gray-400 mt-1">{option.description}</p>
                      </div>
                    </label>
                  )
                })}
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-4">
                Tags (Opcional)
              </label>
              <div className="flex flex-wrap gap-2 mb-4">
                {availableTags.map(tag => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => {
                      if (formData.basicInfo.tags.includes(tag)) {
                        setFormData(prev => ({
                          ...prev,
                          basicInfo: {
                            ...prev.basicInfo,
                            tags: prev.basicInfo.tags.filter(t => t !== tag)
                          }
                        }))
                      } else {
                        setFormData(prev => ({
                          ...prev,
                          basicInfo: {
                            ...prev.basicInfo,
                            tags: [...prev.basicInfo.tags, tag]
                          }
                        }))
                      }
                    }}
                    className={`px-3 py-1 rounded-monumenta text-sm transition-colors ${
                      formData.basicInfo.tags.includes(tag)
                        ? 'bg-brand-primary text-brand-dark'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.basicInfo.tags.map(tag => (
                  <span key={tag} className="px-3 py-1 bg-brand-primary text-brand-dark rounded-monumenta text-sm font-medium">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Descrição */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Descrição *
              </label>
              <textarea
                value={formData.basicInfo.description}
                onChange={(e) => handleInputChange('basicInfo', 'description', e.target.value)}
                rows={4}
                className={`w-full bg-brand-dark border rounded-monumenta px-4 py-3 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none resize-none ${errors.description ? 'border-brand-red' : 'border-gray-700'}`}
                placeholder="Descreva o treinamento, o que será abordado e qual o valor para os participantes..."
              />
              {errors.description && <p className="text-brand-red text-sm mt-1">{errors.description}</p>}
              <p className="text-xs text-gray-400 mt-1">
                {formData.basicInfo.description.length}/500 caracteres
              </p>
            </div>

            {/* Objetivos */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-display font-medium text-brand-primary">
                  Objetivos de Aprendizagem *
                </h3>
                <AddItemButton 
                  onAdd={(value) => addToArray('basicInfo', 'objectives', value)}
                  placeholder="Novo objetivo..."
                />
              </div>
              
              {formData.basicInfo.objectives.length === 0 ? (
                <p className="text-gray-400 text-center py-4">Nenhum objetivo adicionado</p>
              ) : (
                <div className="space-y-2">
                  {formData.basicInfo.objectives.map((objective, index) => (
                    <div key={index} className="flex items-start justify-between bg-brand-dark p-3 rounded-monumenta">
                      <span className="text-white flex-1">{objective}</span>
                      <button
                        onClick={() => removeFromArray('basicInfo', 'objectives', index)}
                        className="text-brand-red hover:text-brand-red/80 ml-2 flex-shrink-0"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {errors.objectives && <p className="text-brand-red text-sm mt-2">{errors.objectives}</p>}
            </div>
          </div>
        )

      case 2:
        return (
          <div className="space-y-6">
            {/* Módulos */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-display font-medium text-brand-primary">
                  Módulos do Treinamento *
                </h3>
                <button
                  onClick={addModule}
                  className="flex items-center px-4 py-2 bg-brand-primary text-brand-dark rounded-monumenta hover:bg-brand-primary/90 transition-colors"
                >
                  <Plus size={16} className="mr-2" />
                  Adicionar Módulo
                </button>
              </div>

              {formData.content.modules.length === 0 ? (
                <p className="text-gray-400 text-center py-4">Nenhum módulo adicionado</p>
              ) : (
                <div className="space-y-4">
                  {formData.content.modules.map((module, index) => (
                    <div key={index} className="bg-brand-dark p-4 rounded-monumenta">
                      <div className="flex items-start justify-between mb-4">
                        <h4 className="font-medium text-white">Módulo {index + 1}</h4>
                        <button
                          onClick={() => removeModule(index)}
                          className="text-brand-red hover:text-brand-red/80"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Título do Módulo
                          </label>
                          <input
                            type="text"
                            value={module.title}
                            onChange={(e) => {
                              const newModules = [...formData.content.modules]
                              newModules[index].title = e.target.value
                              setFormData(prev => ({
                                ...prev,
                                content: { ...prev.content, modules: newModules }
                              }))
                            }}
                            className="w-full bg-brand-dark-light border border-gray-700 rounded-monumenta px-4 py-2 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none"
                            placeholder="Ex: Introdução ao Marketing Digital"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Duração (minutos)
                          </label>
                          <input
                            type="number"
                            value={module.duration}
                            onChange={(e) => {
                              const newModules = [...formData.content.modules]
                              newModules[index].duration = parseInt(e.target.value) || 0
                              setFormData(prev => ({
                                ...prev,
                                content: { ...prev.content, modules: newModules }
                              }))
                            }}
                            className="w-full bg-brand-dark-light border border-gray-700 rounded-monumenta px-4 py-2 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none"
                            min="0"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Descrição
                        </label>
                        <textarea
                          value={module.description}
                          onChange={(e) => {
                            const newModules = [...formData.content.modules]
                            newModules[index].description = e.target.value
                            setFormData(prev => ({
                              ...prev,
                              content: { ...prev.content, modules: newModules }
                            }))
                          }}
                          rows={3}
                          className="w-full bg-brand-dark-light border border-gray-700 rounded-monumenta px-4 py-2 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none resize-none"
                          placeholder="Descreva o que será abordado neste módulo..."
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {errors.modules && <p className="text-brand-red text-sm mt-2">{errors.modules}</p>}
            </div>

            {/* Materiais de Apoio */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <h3 className="text-lg font-display font-medium text-brand-primary mb-4">
                Materiais de Apoio
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                {[
                  { type: 'pdf', icon: FileText, label: 'PDF' },
                  { type: 'video', icon: Video, label: 'Vídeo' },
                  { type: 'link', icon: Link, label: 'Link' },
                  { type: 'arquivo', icon: Upload, label: 'Arquivo' }
                ].map(({ type, icon: Icon, label }) => (
                  <button
                    key={type}
                    className="flex flex-col items-center p-4 border-2 border-dashed border-gray-600 rounded-monumenta hover:border-brand-primary transition-colors"
                  >
                    <Icon size={24} className="text-gray-400 mb-2" />
                    <span className="text-sm text-gray-400">Adicionar {label}</span>
                  </button>
                ))}
              </div>

              {formData.content.materials.length > 0 && (
                <div className="space-y-2">
                  {formData.content.materials.map((material, index) => (
                    <div key={index} className="flex items-center justify-between bg-brand-dark p-3 rounded-monumenta">
                      <div className="flex items-center">
                        {material.type === 'pdf' && <FileText size={16} className="text-brand-primary mr-2" />}
                        {material.type === 'video' && <Video size={16} className="text-brand-primary mr-2" />}
                        {material.type === 'link' && <Link size={16} className="text-brand-primary mr-2" />}
                        {material.type === 'arquivo' && <Upload size={16} className="text-brand-primary mr-2" />}
                        <span className="text-white">{material.name}</span>
                      </div>
                      <button className="text-brand-red hover:text-brand-red/80">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Configurações de Conteúdo */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <h3 className="text-lg font-display font-medium text-brand-primary mb-4">
                Configurações
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-white">Quiz de Avaliação</h4>
                    <p className="text-sm text-gray-400">Inclui quiz para testar conhecimento</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.content.hasQuiz}
                      onChange={(e) => handleInputChange('content', 'hasQuiz', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-primary"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-white">Certificado de Conclusão</h4>
                    <p className="text-sm text-gray-400">Gera certificado ao completar</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.content.hasCertificate}
                      onChange={(e) => handleInputChange('content', 'hasCertificate', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-primary"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Duração Total */}
            <div className="bg-brand-dark-light p-4 rounded-monumenta">
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Duração Total Estimada:</span>
                <span className="text-brand-primary font-medium">
                  {formData.content.modules.reduce((total, module) => total + module.duration, 0)} minutos
                  ({Math.round(formData.content.modules.reduce((total, module) => total + module.duration, 0) / 60)} horas)
                </span>
              </div>
            </div>
          </div>
        )

      case 3:
        return (
          <div className="space-y-6">
            {/* Público-Alvo */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <h3 className="text-lg font-display font-medium text-brand-primary mb-4">
                Cargos/Funções *
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {roles.map(role => (
                  <label key={role} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.audience.targetRoles.includes(role)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({
                            ...prev,
                            audience: {
                              ...prev.audience,
                              targetRoles: [...prev.audience.targetRoles, role]
                            }
                          }))
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            audience: {
                              ...prev.audience,
                              targetRoles: prev.audience.targetRoles.filter(r => r !== role)
                            }
                          }))
                        }
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-5 h-5 border-2 border-gray-400 rounded peer-checked:border-brand-primary peer-checked:bg-brand-primary relative">
                      {formData.audience.targetRoles.includes(role) && (
                        <CheckCircle className="absolute -top-0.5 -left-0.5 text-brand-primary" size={20} />
                      )}
                    </div>
                    <span className="ml-3 text-white">{role}</span>
                  </label>
                ))}
              </div>
              {errors.targetRoles && <p className="text-brand-red text-sm mt-2">{errors.targetRoles}</p>}
            </div>

            {/* Departamentos */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <h3 className="text-lg font-display font-medium text-brand-primary mb-4">
                Departamentos
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {departments.map(dept => (
                  <label key={dept} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.audience.departments.includes(dept)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({
                            ...prev,
                            audience: {
                              ...prev.audience,
                              departments: [...prev.audience.departments, dept]
                            }
                          }))
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            audience: {
                              ...prev.audience,
                              departments: prev.audience.departments.filter(d => d !== dept)
                            }
                          }))
                        }
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-5 h-5 border-2 border-gray-400 rounded peer-checked:border-brand-primary peer-checked:bg-brand-primary relative">
                      {formData.audience.departments.includes(dept) && (
                        <CheckCircle className="absolute -top-0.5 -left-0.5 text-brand-primary" size={20} />
                      )}
                    </div>
                    <span className="ml-3 text-white">{dept}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Pré-requisitos */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-display font-medium text-brand-primary">
                  Pré-requisitos
                </h3>
                <AddItemButton 
                  onAdd={(value) => addToArray('audience', 'prerequisites', value)}
                  placeholder="Novo pré-requisito..."
                />
              </div>
              
              {formData.audience.prerequisites.length === 0 ? (
                <p className="text-gray-400 text-center py-4">Nenhum pré-requisito definido</p>
              ) : (
                <div className="space-y-2">
                  {formData.audience.prerequisites.map((prerequisite, index) => (
                    <div key={index} className="flex items-start justify-between bg-brand-dark p-3 rounded-monumenta">
                      <span className="text-white flex-1">{prerequisite}</span>
                      <button
                        onClick={() => removeFromArray('audience', 'prerequisites', index)}
                        className="text-brand-red hover:text-brand-red/80 ml-2 flex-shrink-0"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Limites de Participantes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Mínimo de Participantes
                </label>
                <input
                  type="number"
                  value={formData.audience.minParticipants}
                  onChange={(e) => handleInputChange('audience', 'minParticipants', parseInt(e.target.value) || 1)}
                  className="w-full bg-brand-dark border border-gray-700 rounded-monumenta px-4 py-3 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Máximo de Participantes
                </label>
                <input
                  type="number"
                  value={formData.audience.maxParticipants}
                  onChange={(e) => handleInputChange('audience', 'maxParticipants', parseInt(e.target.value) || 0)}
                  className="w-full bg-brand-dark border border-gray-700 rounded-monumenta px-4 py-3 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none"
                  min="1"
                  placeholder="Sem limite"
                />
              </div>
            </div>

            {/* Preview do Público */}
            <div className="bg-brand-dark-light p-4 rounded-monumenta">
              <h3 className="text-lg font-display font-medium text-brand-primary mb-2">
                Resumo do Público-Alvo
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-300">Cargos selecionados:</span>
                  <span className="text-brand-primary font-medium">
                    {formData.audience.targetRoles.length} cargos
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Departamentos:</span>
                  <span className="text-brand-primary font-medium">
                    {formData.audience.departments.length} departamentos
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Pré-requisitos:</span>
                  <span className="text-white font-medium">
                    {formData.audience.prerequisites.length} definidos
                  </span>
                </div>
              </div>
            </div>
          </div>
        )

      case 4:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Data de Início */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Data de Início *
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="date"
                    value={formData.schedule.startDate}
                    onChange={(e) => handleInputChange('schedule', 'startDate', e.target.value)}
                    className={`w-full bg-brand-dark border rounded-monumenta pl-10 pr-4 py-3 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none ${errors.startDate ? 'border-brand-red' : 'border-gray-700'}`}
                  />
                  {errors.startDate && <p className="text-brand-red text-sm mt-1">{errors.startDate}</p>}
                </div>
              </div>

              {/* Data de Término */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Data de Término
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="date"
                    value={formData.schedule.endDate}
                    onChange={(e) => handleInputChange('schedule', 'endDate', e.target.value)}
                    className="w-full bg-brand-dark border border-gray-700 rounded-monumenta pl-10 pr-4 py-3 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Sessões */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-display font-medium text-brand-primary">
                  Sessões do Treinamento *
                </h3>
                <button
                  onClick={addSession}
                  className="flex items-center px-4 py-2 bg-brand-primary text-brand-dark rounded-monumenta hover:bg-brand-primary/90 transition-colors"
                >
                  <Plus size={16} className="mr-2" />
                  Adicionar Sessão
                </button>
              </div>

              {formData.schedule.sessions.length === 0 ? (
                <p className="text-gray-400 text-center py-4">Nenhuma sessão adicionada</p>
              ) : (
                <div className="space-y-4">
                  {formData.schedule.sessions.map((session, index) => (
                    <div key={index} className="bg-brand-dark p-4 rounded-monumenta">
                      <div className="flex items-start justify-between mb-4">
                        <h4 className="font-medium text-white">Sessão {index + 1}</h4>
                        <button
                          onClick={() => removeSession(index)}
                          className="text-brand-red hover:text-brand-red/80"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Data
                          </label>
                          <input
                            type="date"
                            value={session.date}
                            onChange={(e) => {
                              const newSessions = [...formData.schedule.sessions]
                              newSessions[index].date = e.target.value
                              setFormData(prev => ({
                                ...prev,
                                schedule: { ...prev.schedule, sessions: newSessions }
                              }))
                            }}
                            className="w-full bg-brand-dark-light border border-gray-700 rounded-monumenta px-4 py-2 text-white focus:border-brand-primary focus:outline-none"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Horário de Início
                          </label>
                          <input
                            type="time"
                            value={session.startTime}
                            onChange={(e) => {
                              const newSessions = [...formData.schedule.sessions]
                              newSessions[index].startTime = e.target.value
                              setFormData(prev => ({
                                ...prev,
                                schedule: { ...prev.schedule, sessions: newSessions }
                              }))
                            }}
                            className="w-full bg-brand-dark-light border border-gray-700 rounded-monumenta px-4 py-2 text-white focus:border-brand-primary focus:outline-none"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Horário de Término
                          </label>
                          <input
                            type="time"
                            value={session.endTime}
                            onChange={(e) => {
                              const newSessions = [...formData.schedule.sessions]
                              newSessions[index].endTime = e.target.value
                              setFormData(prev => ({
                                ...prev,
                                schedule: { ...prev.schedule, sessions: newSessions }
                              }))
                            }}
                            className="w-full bg-brand-dark-light border border-gray-700 rounded-monumenta px-4 py-2 text-white focus:border-brand-primary focus:outline-none"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Local/Plataforma
                          </label>
                          <input
                            type="text"
                            value={session.location}
                            onChange={(e) => {
                              const newSessions = [...formData.schedule.sessions]
                              newSessions[index].location = e.target.value
                              setFormData(prev => ({
                                ...prev,
                                schedule: { ...prev.schedule, sessions: newSessions }
                              }))
                            }}
                            className="w-full bg-brand-dark-light border border-gray-700 rounded-monumenta px-4 py-2 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none"
                            placeholder="Sala de reunião, Zoom, Teams..."
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Instrutor
                          </label>
                          <select
                            value={session.instructor}
                            onChange={(e) => {
                              const newSessions = [...formData.schedule.sessions]
                              newSessions[index].instructor = e.target.value
                              setFormData(prev => ({
                                ...prev,
                                schedule: { ...prev.schedule, sessions: newSessions }
                              }))
                            }}
                            className="w-full bg-brand-dark-light border border-gray-700 rounded-monumenta px-4 py-2 text-white focus:border-brand-primary focus:outline-none"
                          >
                            <option value="">Selecione o instrutor</option>
                            {instructors.map(instructor => (
                              <option key={instructor.id} value={instructor.id}>
                                {instructor.name} - {instructor.specialty}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {errors.sessions && <p className="text-brand-red text-sm mt-2">{errors.sessions}</p>}
            </div>

            {/* Treinamento Recorrente */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-display font-medium text-brand-primary">
                    Treinamento Recorrente
                  </h3>
                  <p className="text-sm text-gray-400">Repetir este treinamento periodicamente</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.schedule.isRecurring}
                    onChange={(e) => handleInputChange('schedule', 'isRecurring', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-primary"></div>
                </label>
              </div>

              {formData.schedule.isRecurring && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Padrão de Recorrência
                  </label>
                  <select
                    value={formData.schedule.recurringPattern}
                    onChange={(e) => handleInputChange('schedule', 'recurringPattern', e.target.value)}
                    className="w-full bg-brand-dark border border-gray-700 rounded-monumenta px-4 py-3 text-white focus:border-brand-primary focus:outline-none"
                  >
                    <option value="">Selecione a frequência</option>
                    <option value="monthly">Mensal</option>
                    <option value="quarterly">Trimestral</option>
                    <option value="biannual">Semestral</option>
                    <option value="annual">Anual</option>
                  </select>
                </div>
              )}
            </div>
          </div>
        )

      case 5:
        return (
          <div className="space-y-6">
            {/* Instrutor */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <h3 className="text-lg font-display font-medium text-brand-primary mb-4">
                Instrutor Principal *
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Instrutor
                  </label>
                  <select
                    value={formData.resources.instructor.name}
                    onChange={(e) => {
                      const instructor = instructors.find(i => i.id === e.target.value)
                      if (instructor) {
                        handleNestedInputChange('resources', 'instructor', 'name', instructor.name)
                        handleNestedInputChange('resources', 'instructor', 'bio', instructor.bio)
                      }
                    }}
                    className={`w-full bg-brand-dark border rounded-monumenta px-4 py-3 text-white focus:border-brand-primary focus:outline-none ${errors.instructor ? 'border-brand-red' : 'border-gray-700'}`}
                  >
                    <option value="">Selecione o instrutor</option>
                    {instructors.map(instructor => (
                      <option key={instructor.id} value={instructor.id}>
                        {instructor.name} - {instructor.specialty}
                      </option>
                    ))}
                  </select>
                  {errors.instructor && <p className="text-brand-red text-sm mt-1">{errors.instructor}</p>}
                </div>
              </div>

              {formData.resources.instructor.name && (
                <div className="bg-brand-dark p-4 rounded-monumenta">
                  <h4 className="font-medium text-white mb-2">{formData.resources.instructor.name}</h4>
                  <p className="text-gray-300 text-sm">{formData.resources.instructor.bio}</p>
                </div>
              )}
            </div>

            {/* Orçamento */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Orçamento (R$)
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="number"
                    value={formData.resources.budget}
                    onChange={(e) => handleInputChange('resources', 'budget', parseFloat(e.target.value) || 0)}
                    className="w-full bg-brand-dark border border-gray-700 rounded-monumenta pl-10 pr-4 py-3 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Local/Plataforma
                </label>
                <input
                  type="text"
                  value={formData.resources.location}
                  onChange={(e) => handleInputChange('resources', 'location', e.target.value)}
                  className="w-full bg-brand-dark border border-gray-700 rounded-monumenta px-4 py-3 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none"
                  placeholder="Sala de treinamento, Zoom, Teams..."
                />
              </div>
            </div>

            {/* Equipamentos Necessários */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-display font-medium text-brand-primary">
                  Equipamentos/Recursos Necessários
                </h3>
                <AddItemButton 
                  onAdd={(value) => addToArray('resources', 'equipment', value)}
                  placeholder="Novo equipamento..."
                />
              </div>
              
              {formData.resources.equipment.length === 0 ? (
                <p className="text-gray-400 text-center py-4">Nenhum equipamento especificado</p>
              ) : (
                <div className="space-y-2">
                  {formData.resources.equipment.map((equipment, index) => (
                    <div key={index} className="flex items-start justify-between bg-brand-dark p-3 rounded-monumenta">
                      <span className="text-white flex-1">{equipment}</span>
                      <button
                        onClick={() => removeFromArray('resources', 'equipment', index)}
                        className="text-brand-red hover:text-brand-red/80 ml-2 flex-shrink-0"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Plataforma para Online */}
            {(formData.basicInfo.type === 'online' || formData.basicInfo.type === 'hibrido') && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Plataforma Online
                </label>
                <select
                  value={formData.resources.platform}
                  onChange={(e) => handleInputChange('resources', 'platform', e.target.value)}
                  className="w-full bg-brand-dark border border-gray-700 rounded-monumenta px-4 py-3 text-white focus:border-brand-primary focus:outline-none"
                >
                  <option value="">Selecione a plataforma</option>
                  <option value="zoom">Zoom</option>
                  <option value="teams">Microsoft Teams</option>
                  <option value="meet">Google Meet</option>
                  <option value="webex">Cisco Webex</option>
                  <option value="custom">Plataforma personalizada</option>
                </select>
              </div>
            )}

            {/* Resumo de Custos */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <h3 className="text-lg font-display font-medium text-brand-primary mb-4">
                Resumo de Custos
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-300">Orçamento total:</span>
                  <span className="text-brand-primary font-medium">
                    R$ {formData.resources.budget.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Custo por participante:</span>
                  <span className="text-white font-medium">
                    {formData.audience.maxParticipants > 0 
                      ? `R$ ${(formData.resources.budget / formData.audience.maxParticipants).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                      : 'N/A'
                    }
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Duração total:</span>
                  <span className="text-white font-medium">
                    {Math.round(formData.content.modules.reduce((total, module) => total + module.duration, 0) / 60)} horas
                  </span>
                </div>
              </div>
            </div>
          </div>
        )

      case 6:
        return (
          <div className="space-y-6">
            {/* Avaliações */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <h3 className="text-lg font-display font-medium text-brand-primary mb-4">
                Configurações de Avaliação
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-white">Avaliação Prévia</h4>
                    <p className="text-sm text-gray-400">Avaliar conhecimento antes do treinamento</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.assessment.hasPreAssessment}
                      onChange={(e) => handleInputChange('assessment', 'hasPreAssessment', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-primary"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-white">Avaliação Final</h4>
                    <p className="text-sm text-gray-400">Avaliar conhecimento após o treinamento</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.assessment.hasPostAssessment}
                      onChange={(e) => handleInputChange('assessment', 'hasPostAssessment', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-primary"></div>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nota Mínima para Aprovação (%)
                  </label>
                  <input
                    type="number"
                    value={formData.assessment.passingScore}
                    onChange={(e) => handleInputChange('assessment', 'passingScore', parseInt(e.target.value) || 70)}
                    className="w-full bg-brand-dark border border-gray-700 rounded-monumenta px-4 py-3 text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none"
                    min="0"
                    max="100"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-white">Multiplicação de Conhecimento</h4>
                    <p className="text-sm text-gray-400">Participante deve treinar outros colaboradores</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.assessment.knowledgeMultiplication}
                      onChange={(e) => handleInputChange('assessment', 'knowledgeMultiplication', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-primary"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Critérios de Avaliação */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-display font-medium text-brand-primary">
                  Critérios de Avaliação
                </h3>
                <AddItemButton 
                  onAdd={(value) => addToArray('assessment', 'evaluationCriteria', value)}
                  placeholder="Novo critério..."
                />
              </div>
              
              {formData.assessment.evaluationCriteria.length === 0 ? (
                <p className="text-gray-400 text-center py-4">Nenhum critério definido</p>
              ) : (
                <div className="space-y-2">
                  {formData.assessment.evaluationCriteria.map((criteria, index) => (
                    <div key={index} className="flex items-start justify-between bg-brand-dark p-3 rounded-monumenta">
                      <span className="text-white flex-1">{criteria}</span>
                      <button
                        onClick={() => removeFromArray('assessment', 'evaluationCriteria', index)}
                        className="text-brand-red hover:text-brand-red/80 ml-2 flex-shrink-0"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Template de Certificado */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Template de Certificado
              </label>
              <select
                value={formData.assessment.certificateTemplate}
                onChange={(e) => handleInputChange('assessment', 'certificateTemplate', e.target.value)}
                className="w-full bg-brand-dark border border-gray-700 rounded-monumenta px-4 py-3 text-white focus:border-brand-primary focus:outline-none"
              >
                <option value="">Selecione o template</option>
                <option value="standard">Padrão Monumenta</option>
                <option value="technical">Técnico</option>
                <option value="leadership">Liderança</option>
                <option value="compliance">Compliance</option>
                <option value="custom">Personalizado</option>
              </select>
            </div>
          </div>
        )

      case 7:
        return (
          <div className="space-y-6">
            {/* Preview do Treinamento */}
            <div className="bg-brand-dark-light p-6 rounded-monumenta">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-display font-bold text-white mb-2">
                    {formData.basicInfo.title}
                  </h2>
                  <div className="flex items-center space-x-4 text-sm text-gray-400 mb-2">
                    <span className="flex items-center">
                      <Target size={16} className="mr-1" />
                      {formData.basicInfo.category}
                    </span>
                    <span className="flex items-center">
                      <Clock size={16} className="mr-1" />
                      {Math.round(formData.content.modules.reduce((total, module) => total + module.duration, 0) / 60)}h
                    </span>
                    <span className="flex items-center">
                      <Users size={16} className="mr-1" />
                      {formData.basicInfo.type === 'online' ? 'Online' : formData.basicInfo.type === 'hibrido' ? 'Híbrido' : 'Presencial'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded-monumenta text-xs font-medium ${
                      formData.basicInfo.level === 'iniciante' ? 'bg-green-900 text-green-300' :
                      formData.basicInfo.level === 'intermediario' ? 'bg-yellow-900 text-yellow-300' :
                      'bg-red-900 text-red-300'
                    }`}>
                      {formData.basicInfo.level}
                    </span>
                    {formData.basicInfo.tags.map(tag => (
                      <span key={tag} className="px-2 py-1 bg-brand-primary/20 text-brand-primary rounded-monumenta text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                {formData.resources.budget > 0 && (
                  <div className="text-right">
                    <div className="text-2xl font-bold text-brand-primary">
                      R$ {formData.resources.budget.toLocaleString('pt-BR')}
                    </div>
                    <div className="text-sm text-gray-400">Orçamento total</div>
                  </div>
                )}
              </div>

              <p className="text-gray-300 mb-6">{formData.basicInfo.description}</p>

              {/* Objetivos */}
              {formData.basicInfo.objectives.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-display font-medium text-brand-primary mb-3">Objetivos de Aprendizagem:</h3>
                  <ul className="space-y-2">
                    {formData.basicInfo.objectives.map((objective, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle size={16} className="text-brand-primary mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-300">{objective}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Módulos */}
              {formData.content.modules.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-display font-medium text-brand-primary mb-3">Conteúdo Programático:</h3>
                  <div className="space-y-3">
                    {formData.content.modules.map((module, index) => (
                      <div key={index} className="bg-brand-dark p-3 rounded-monumenta">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-white">{module.title}</h4>
                          <span className="text-sm text-brand-primary">{module.duration} min</span>
                        </div>
                        {module.description && (
                          <p className="text-gray-400 text-sm mt-1">{module.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Público-Alvo */}
              <div className="mb-6">
                <h3 className="font-display font-medium text-brand-primary mb-3">Público-Alvo:</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-gray-400 text-sm">Cargos:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {formData.audience.targetRoles.slice(0, 3).map(role => (
                        <span key={role} className="px-2 py-1 bg-gray-700 text-white rounded text-xs">
                          {role}
                        </span>
                      ))}
                      {formData.audience.targetRoles.length > 3 && (
                        <span className="px-2 py-1 bg-gray-700 text-white rounded text-xs">
                          +{formData.audience.targetRoles.length - 3} mais
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-400 text-sm">Departamentos:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {formData.audience.departments.slice(0, 3).map(dept => (
                        <span key={dept} className="px-2 py-1 bg-gray-700 text-white rounded text-xs">
                          {dept}
                        </span>
                      ))}
                      {formData.audience.departments.length > 3 && (
                        <span className="px-2 py-1 bg-gray-700 text-white rounded text-xs">
                          +{formData.audience.departments.length - 3} mais
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Estatísticas */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-brand-dark-light p-4 rounded-monumenta text-center">
                <div className="text-2xl font-bold text-brand-primary">
                  {formData.content.modules.length}
                </div>
                <div className="text-sm text-gray-400">Módulos</div>
              </div>

              <div className="bg-brand-dark-light p-4 rounded-monumenta text-center">
                <div className="text-2xl font-bold text-brand-primary">
                  {Math.round(formData.content.modules.reduce((total, module) => total + module.duration, 0) / 60)}h
                </div>
                <div className="text-sm text-gray-400">Duração</div>
              </div>

              <div className="bg-brand-dark-light p-4 rounded-monumenta text-center">
                <div className="text-2xl font-bold text-brand-primary">
                  {formData.schedule.sessions.length}
                </div>
                <div className="text-sm text-gray-400">Sessões</div>
              </div>

              <div className="bg-brand-dark-light p-4 rounded-monumenta text-center">
                <div className="text-2xl font-bold text-brand-primary">
                  {formData.audience.maxParticipants || '∞'}
                </div>
                <div className="text-sm text-gray-400">Max. Participantes</div>
              </div>
            </div>

            {/* Cronograma */}
            {formData.schedule.sessions.length > 0 && (
              <div className="bg-brand-dark-light p-6 rounded-monumenta">
                <h3 className="text-lg font-display font-medium text-brand-primary mb-4">
                  Cronograma de Sessões
                </h3>
                <div className="space-y-3">
                  {formData.schedule.sessions.map((session, index) => (
                    <div key={index} className="flex items-center justify-between bg-brand-dark p-3 rounded-monumenta">
                      <div>
                        <div className="font-medium text-white">Sessão {index + 1}</div>
                        <div className="text-sm text-gray-400">
                          {session.date && new Date(session.date).toLocaleDateString('pt-BR')} • 
                          {session.startTime} - {session.endTime}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-brand-primary">{session.location}</div>
                        <div className="text-xs text-gray-400">{session.instructor}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Confirmação */}
            <div className="bg-green-900/20 border border-green-500/50 rounded-monumenta p-4">
              <div className="flex">
                <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} />
                <div>
                  <h4 className="text-green-500 font-medium mb-1">Treinamento pronto para criação</h4>
                  <p className="text-green-100 text-sm">
                    Todos os campos obrigatórios foram preenchidos. O treinamento será criado e ficará 
                    disponível para inscrições imediatamente.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  // Component for adding items to arrays (reused from recruitment page)
  const AddItemButton = ({ onAdd, placeholder }: { onAdd: (value: string) => void; placeholder: string }) => {
    const [inputValue, setInputValue] = useState('')
    const [isOpen, setIsOpen] = useState(false)

    const handleAdd = () => {
      if (inputValue.trim()) {
        onAdd(inputValue)
        setInputValue('')
        setIsOpen(false)
      }
    }

    if (!isOpen) {
      return (
        <button
          onClick={() => setIsOpen(true)}
          className="flex items-center px-4 py-2 bg-brand-primary text-brand-dark rounded-monumenta hover:bg-brand-primary/90 transition-colors"
        >
          <Plus size={16} className="mr-2" />
          Adicionar
        </button>
      )
    }

    return (
      <div className="flex items-center space-x-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={placeholder}
          className="px-3 py-2 bg-brand-dark border border-gray-700 rounded-monumenta text-white placeholder-gray-400 focus:border-brand-primary focus:outline-none"
          onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
          autoFocus
        />
        <button
          onClick={handleAdd}
          className="px-3 py-2 bg-brand-primary text-brand-dark rounded-monumenta hover:bg-brand-primary/90 transition-colors"
        >
          <CheckCircle size={16} />
        </button>
        <button
          onClick={() => {
            setInputValue('')
            setIsOpen(false)
          }}
          className="px-3 py-2 text-gray-400 hover:text-white transition-colors"
        >
          ×
        </button>
      </div>
    )
  }

  return (
    
            <div className="max-w-4xl mx-auto">
              {/* Header */}
              <div className="mb-8">
                <div className="flex justify-end mb-4">
                  <button
                    onClick={() => window.location.href = '/training'}
                    className="flex items-center px-4 py-2 text-gray-400 hover:text-brand-primary transition-colors"
                  >
                    <ArrowLeft size={20} className="mr-2" />
                    Voltar
                  </button>
                </div>

                {/* Progress Steps */}
                <div className="flex items-center space-x-2 mb-8 overflow-x-auto">
                  {steps.map((step, index) => {
                    const Icon = step.icon
                    const isActive = currentStep === step.id
                    const isCompleted = currentStep > step.id
                    
                    return (
                      <div key={step.id} className="flex items-center flex-shrink-0">
                        <div className={`
                    flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors
                    ${isActive 
                            ? 'border-brand-primary bg-brand-primary text-brand-dark' 
                            : isCompleted 
                              ? 'border-brand-primary bg-brand-primary text-brand-dark'
                              : 'border-gray-600 text-gray-400'
                          }
                  `}>
                          {isCompleted ? (
                            <CheckCircle size={20} />
                          ) : (
                            <Icon size={20} />
                          )}
                        </div>
                        <div className="ml-2 min-w-0">
                          <p className={`text-xs font-medium whitespace-nowrap ${isActive ? 'text-brand-primary' : isCompleted ? 'text-brand-primary' : 'text-gray-400'}`}>
                            {step.title}
                          </p>
                        </div>
                        {index < steps.length - 1 && (
                          <div className={`w-6 h-0.5 ml-2 ${isCompleted ? 'bg-brand-primary' : 'bg-gray-600'}`} />
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Form Content */}
              <div className="bg-brand-dark-light rounded-monumenta p-8">
                {renderStepContent()}

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-8 pt-6 border-t border-gray-700">
                  <button
                    onClick={handlePrevious}
                    disabled={currentStep === 1}
                    className="flex items-center px-6 py-3 bg-gray-700 text-white rounded-monumenta hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArrowLeft size={20} className="mr-2" />
                    Anterior
                  </button>

                  {currentStep < steps.length ? (
                    <button
                      onClick={handleNext}
                      className="flex items-center px-6 py-3 bg-brand-primary text-brand-dark rounded-monumenta hover:bg-brand-primary/90 transition-colors font-medium"
                    >
                      Próximo
                      <ArrowRight size={20} className="ml-2" />
                    </button>
                  ) : (
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className="flex items-center px-6 py-3 bg-brand-primary text-brand-dark rounded-monumenta hover:bg-brand-primary/90 transition-colors font-medium disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-dark mr-2"></div>
                          Criando...
                        </>
                      ) : (
                        <>
                          <Save size={20} className="mr-2" />
                          Criar Treinamento
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          
  )
} 